/**
 * @file PlanTypeCategoriesItemPage.js
 * @description Displays and manages plan type category details with drag and drop functionality for sorting.
 */

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';

import styles from './PlanYear.module.scss';
import {
    fetchPlanTypeDetail,
    updatePlanTypeDetail,
} from '../../services/benefitPlanService';
import { ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';
const PlanTypeCategoriesItemPage = () => {
    const { showModal } = useContext(ModalContext);
    const { planTypeId, categoryId } = useParams();
    const navigate = useNavigate();
    const [planTypeDetails, setPlanTypeDetails] = useState([]);
    const [originalPlanTypeDetails, setOriginalPlanTypeDetails] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [categoryName, setCategoryName] = useState('');
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);

    const loadPlanTypeDetails = useCallback(async () => {
        try {
            setIsLoading(true);
            const fetchedDetails = await fetchPlanTypeDetail(categoryId);

            const mappedDetails = fetchedDetails.map(detail => ({
                ...detail,
                detailId: detail.id,
                detailName: detail.systemPlanDetail?.name,
                isVisible: detail.isActive,
            }));

            const sortedDetails = mappedDetails.sort(
                (a, b) => a.sortOrder - b.sortOrder,
            );
            setPlanTypeDetails(sortedDetails);
            setOriginalPlanTypeDetails(sortedDetails);

            if (fetchedDetails.length > 0) {
                const firstDetail = fetchedDetails[0];
                setCategoryName(
                    firstDetail.systemPlanDetail.systemPlanDetailCategory.name,
                );
            }
        } catch (error) {
            console.error('Failed to fetch plan type details', error);
            if (error?.response?.data?.detail) {
                Toast.error(error?.response?.data?.detail);
            } else {
                Toast.error(
                    getText('msg_011ec93a1c94'),
                );
            }
        } finally {
            setIsLoading(false);
        }
    }, [categoryId, planTypeId]);

    const checkForChanges = useCallback((currentDetails, originalDetails) => {
        if (currentDetails.length !== originalDetails.length) return true;

        return currentDetails.some((detail, index) => {
            const original = originalDetails[index];
            return (
                detail.sortOrder !== original.sortOrder ||
                detail.isVisible !== original.isVisible ||
                detail.detailId !== original.detailId
            );
        });
    }, []);

    useEffect(() => {
        if (categoryId) {
            loadPlanTypeDetails();
        }
    }, [loadPlanTypeDetails, categoryId]);

    useEffect(() => {
        setHasChanges(
            checkForChanges(planTypeDetails, originalPlanTypeDetails),
        );
    }, [planTypeDetails, originalPlanTypeDetails, checkForChanges]);

    const handleToggleVisibility = detail => {
        const updatedDetails = planTypeDetails.map(d =>
            d.detailId === detail.detailId
                ? { ...d, isVisible: !d.isVisible }
                : d,
        );
        setPlanTypeDetails(updatedDetails);
    };

    const handleDragStart = e => {
        const index = e.target.dataset.index;
        setDraggedIndex(index);
        e.dataTransfer.setData('text/plain', index);
        e.target.classList.add('dragging');
    };

    const handleDragOver = e => {
        e.preventDefault();
    };

    const handleDrop = async e => {
        e.preventDefault();
        const targetIndex = hoveredIndex;

        let newIndex;
        if (draggedIndex < targetIndex) {
            newIndex = targetIndex - 1;
        } else {
            newIndex = targetIndex;
        }

        if (newIndex < 0) {
            newIndex = 0;
        }

        if (draggedIndex !== newIndex) {
            const newDetails = [...planTypeDetails];
            const [draggedDetail] = newDetails.splice(draggedIndex, 1);
            newDetails.splice(newIndex, 0, draggedDetail);

            const updatedDetails = newDetails.map((detail, index) => ({
                ...detail,
                sortOrder: index + 1,
            }));

            setPlanTypeDetails(updatedDetails);
        }

        setDraggedIndex(null);
        setHoveredIndex(null);
    };

    const handleDragEnd = e => {
        e.target.classList.remove('dragging');
        setDraggedIndex(null);
        setHoveredIndex(null);
    };

    const handleReset = () => {
        setPlanTypeDetails([...originalPlanTypeDetails]);
        setHasChanges(false);
        Toast.info(getText('msg_deabca949630'));
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);

            const updateData = planTypeDetails.map(detail => ({
                id: detail.id,
                planTypeDetailCategoryId: detail.planTypeDetailCategoryId,
                systemPlanDetailId: detail.systemPlanDetailId,
                sortOrder: detail.sortOrder,
                isActive: detail.isVisible,
            }));

            await updatePlanTypeDetail(categoryId, updateData);

            setOriginalPlanTypeDetails(planTypeDetails);
            setHasChanges(false);
            Toast.success(getText('msg_670010e912c8'));
        } catch (error) {
            console.error('Failed to save plan type details', error);
            if (error?.response?.data?.detail) {
                Toast.error(error?.response?.data?.detail);
            } else {
                Toast.error(getText('msg_ae7d3d06dfce'));
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (hasChanges) {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_b78130c92f32'),
                subtitle: getText('msg_2f4e8fc9ac09'),
                text: getText('msg_f3cefc556397'),
                onYes: () =>
                    navigate(ROUTE_BUILDERS.planTypeCategories(planTypeId)),
                onNo: () => {},
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: getText('msg_36fff63ccbcd'),
            });
        } else {
            navigate(ROUTE_BUILDERS.planTypeCategories(planTypeId));
        }
    };

    const renderPlanTypeDetails = () => {
        if (!planTypeDetails.length) {
            return (
                <NotFoundBox
                    backgroundColor="#f0f4ff"
                    title={getText('msg_192025b17c45')}
                    description={getText('msg_fa66f11395d3')}
                />
            );
        }

        const items = [];

        planTypeDetails.forEach((detail, index) => {
            items.push(
                <div key={detail.detailId} className="mb-3">
                    <div className="row align-items-center">
                        <div className="col-9">
                            <div
                                className={`card shadow-sm rounded-3 ${
                                    hoveredIndex === index
                                        ? 'border-primary border-2'
                                        : ''
                                }`}
                                draggable
                                data-index={index}
                                onDragStart={handleDragStart}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onDragEnd={handleDragEnd}
                                onDragEnter={() => setHoveredIndex(index)}
                                onDragLeave={() => setHoveredIndex(null)}
                            >
                                <div className="card-body pe-0 py-2">
                                    <div className="d-flex align-items-center">
                                        <div className="me-3">
                                            <i className="bi bi-grip-vertical text-muted fs-5" />
                                        </div>
                                        <div>
                                            <h5 className="card-title mb-1">
                                                {detail.detailName}
                                            </h5>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-3">
                            <div className="d-flex justify-content-center">
                                <div className="form-switch form-switch-locked">
                                    <input
                                        className="form-check-input custom-switch-input"
                                        type="checkbox"
                                        checked={detail.isVisible}
                                        onChange={() =>
                                            handleToggleVisibility(detail)
                                        }
                                        onClick={e => e.stopPropagation()}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
            );

            items.push(
                <div
                    key={`drop-zone-${index}`}
                    className={`mb-2 d-flex align-items-center justify-content-center rounded ${
                        hoveredIndex === index + 1
                            ? 'bg-info bg-opacity-10 border border-2 border-primary border-opacity-50 p-3'
                            : 'p-1'
                    }`}
                    data-index={index + 1}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragEnter={() => setHoveredIndex(index + 1)}
                    onDragLeave={() => setHoveredIndex(null)}
                >
                    {hoveredIndex === index + 1 && (
                        <span className="text-muted small">
                            <i className="bi bi-arrow-down me-2" />
                            {getText('msg_7c67e6fb70f3')}
                        </span>
                    )}
                </div>,
            );
        });

        return items;
    };

    if (isLoading) {
        return (
            <div className="page-content">
                <div className="card">
                    <div className="card-body px-0 py-4">
                        <div className="text-center mt-5">
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100%',
            }}
        >
            <div
                className="page-content"
                style={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <div
                    className="card"
                    style={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div
                        className="card-body px-0 py-4"
                        style={{ flexGrow: 1 }}
                    >
                        <div className="ms-3 me-4 d-flex justify-content-between align-items-center py-3">
                            <div className="d-flex flex-column align-items-start mb-4">
                                <p className="page-title card-title ms-4 mb-0">
                                    {getText('msg_c9d009e074da')} {categoryName}
                                </p>
                                <p
                                    className="ing-portals-subtitle mt-1"
                                    style={{ marginTop: '10px' }}
                                >
                                    {getText('msg_ca4971449717')}
                                </p>
                            </div>
                            <div className="float-end d-flex align-items-center align-self-baseline">
                                <span
                                    type="button"
                                    onClick={handleReset}
                                    className="ing-text-buttons mx-3"
                                    style={{
                                        opacity: hasChanges ? 1 : 0.5,
                                        pointerEvents: hasChanges
                                            ? 'auto'
                                            : 'none',
                                    }}
                                >
                                    <i className="bi bi-arrow-counterclockwise me-2" />
                                    {getText('msg_44c57abd888a')}
                                </span>
                                <span
                                    type="button"
                                    onClick={handleSave}
                                    className="ing-text-buttons me-3 d-flex"
                                    style={{
                                        opacity: hasChanges ? 1 : 0.5,
                                        pointerEvents: hasChanges
                                            ? 'auto'
                                            : 'none',
                                    }}
                                >
                                    {isSaving ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            {getText('msg_ae7e887517b0')}
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-check me-2" />
                                            {getText('msg_efc007a393f6')}
                                        </>
                                    )}
                                </span>
                                <span
                                    type="button"
                                    onClick={handleClose}
                                    className="ing-text-buttons me-3 d-flex"
                                    tabIndex={12}
                                >
                                    <i className="bi bi-x me-2" />
                                    {getText('msg_bbfa773e5a63')}
                                </span>
                            </div>
                        </div>

                        {planTypeDetails.length === 0 ? (
                            <NotFoundBox
                                backgroundColor="#f0f4ff"
                                title={getText('msg_192025b17c45')}
                                description={getText('msg_fa66f11395d3')}
                            />
                        ) : (
                            <div className="container-fluid px-4">
                                <div className="row justify-content-center">
                                    <div className="col-12 col-lg-8 mt-4">
                                        <div className="row align-items-center">
                                            <div className="col-9"></div>
                                            <div className="col-3">
                                                <div className="d-flex justify-content-center">
                                                    <h6 className="mb-0">
                                                        {getText('msg_1fe59390acc1')}
                                                    </h6>
                                                </div>
                                            </div>
                                        </div>

                                        {renderPlanTypeDetails()}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlanTypeCategoriesItemPage;
